import { getLogger } from '@logtape/logtape';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { ApiError, api } from '@/lib/api-client.ts';

import { StackOptions } from '@/models/call-system/ProfessionalRoomAssignment.ts';
import { DispatchTicketDto } from '@/models/call-system/dtos/dispatch-ticket-dto.ts';
import { PrintResponseDto } from '@/models/call-system/dtos/print-ticket-dto.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';
import CallSystemUser from '@/models/call-system/models/CallSystemUser.ts';

import { ServiceUrl } from '@/services/ServiceURL.ts';

import { getCustomerId } from '@/utils/customer-utils.ts';
import { serializeApiError } from '@/utils/log-utils.ts';

const logger = getLogger(['service', 'call-system']);

interface ExtAuthResponse {
    token: string;
}

interface GetStackOptionsResponse {
    data: StackOptions;
}

export const updateUserByNumber = async (
    user_info: UserAppointmentDto
): Promise<[boolean, CallSystemUser | ApiError | undefined]> => {
    const config = {
        headers: {
            customer_id: getCustomerId(),
        },
    };
    try {
        const res = await api.post<CallSystemUser>(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.CALL_SYSTEM_API}/users`,
            user_info,
            config
        );
        logger.debug('Call-system user updated', { response: res });
        return [true, res.data];
    } catch (error) {
        return [false, error as ApiError];
    }
};

export const getExtAuthToken = async (
    instance_id: string,
    is_cloud_printer_system: boolean
): Promise<[boolean, string | undefined]> => {
    const config: AxiosRequestConfig = {
        headers: {
            customer_id: getCustomerId(),
            version: is_cloud_printer_system ? 'v2' : 'v1',
        },
        params: {
            instance_id: instance_id,
        },
    };

    try {
        const response: AxiosResponse<ExtAuthResponse> = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.CALL_SYSTEM_API}/ext/auth`,
            config
        );
        return [true, response.data?.token];
    } catch (error) {
        logger.debug('Failed to fetch the call-system ext auth token', { error: error, ...serializeApiError(error) });
        return [false, undefined];
    }
};

export const printTicket = async (
    instance_id: string,
    access_token: string,
    printer_id: string
): Promise<[boolean, number | undefined]> => {
    try {
        const response: AxiosResponse<PrintResponseDto | undefined> = await api.post(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.CALL_SYSTEM_API}/ext/print-ticket`,
            {
                printer_id: printer_id,
                token: access_token,
            },
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    instance_id: instance_id,
                },
            }
        );
        logger.debug('Call-system ticket printed', { response: response });
        return [true, response?.data?.id];
    } catch (error) {
        const apiError = error as ApiError;
        logger.warn('Failed to print the call-system ticket', { apiError: apiError, ...serializeApiError(apiError) });
        return [false, undefined];
    }
};

export const dispatchTicket = async (
    instance_id: string,
    access_token: string,
    ticket_id: string,
    group_id: string
): Promise<boolean> => {
    try {
        const response: AxiosResponse = await api.post<DispatchTicketDto>(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.CALL_SYSTEM_API}/ext/dispatch-ticket`,
            {
                ticket_id: ticket_id,
                group_id: group_id,
                token: access_token,
            },
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    instance_id: instance_id,
                },
            }
        );
        logger.debug('Call-system ticket dispatched', { response: response });
        return true;
    } catch (error) {
        const apiError = error as ApiError;
        logger.warn('Failed to dispatch the call-system ticket', {
            apiError: apiError,
            ...serializeApiError(apiError),
        });
        return false;
    }
};

export const getCurrentStacksForProfessional = async (professionalId: string): Promise<StackOptions | undefined> => {
    try {
        const response: AxiosResponse<GetStackOptionsResponse | undefined> = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.CALL_SYSTEM_API}/stacks`,
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    professional_id: professionalId,
                },
            }
        );
        logger.debug('Call-system stacks fetched', { response: response });
        return response.data?.data;
    } catch (error) {
        const apiError = error as ApiError;
        logger.warn('Failed to fetch the call-system stacks', { apiError: apiError, ...serializeApiError(apiError) });
        return undefined;
    }
};
